import React, { useState } from 'react';
import { PRODUCT_CATEGORIES_OPTIMIZED } from '../data/productCategoriesOptimized';
import MultiLevelProductSelector from './MultiLevelProductSelector';

const TestProductDropdowns = () => {
  // Default to Women Clothing
  const defaultCategory = PRODUCT_CATEGORIES_OPTIMIZED.find(cat => cat.value === 'women-clothing') || PRODUCT_CATEGORIES_OPTIMIZED[1];
  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [selectedType, setSelectedType] = useState(null);
  const [showTip, setShowTip] = useState(false);

  const handleTypeChange = (typeData) => {
    setSelectedType(typeData);
    console.log('Selected type data:', typeData);
  };

  return (
    <div className="p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-purple-800">Test Product Type Dropdowns</h1>
      
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-3 text-gray-700">Product Category</h2>
          <select
            className="w-full border border-gray-300 rounded-lg px-3 py-2"
            value={selectedCategory.value}
            onChange={(e) => {
              const category = PRODUCT_CATEGORIES_OPTIMIZED.find(cat => cat.value === e.target.value);
              setSelectedCategory(category);
              setSelectedType(null);
            }}
          >
            {PRODUCT_CATEGORIES_OPTIMIZED.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        <div>
          <h2 className="text-lg font-semibold mb-3 text-gray-700">Product Type (with Subtype Options)</h2>
          <MultiLevelProductSelector
            selectedCategory={selectedCategory}
            selectedType={selectedType}
            onTypeChange={handleTypeChange}
            showTip={showTip}
            onTipToggle={() => setShowTip(!showTip)}
            size="default"
          />
        </div>

        {selectedType && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold mb-2 text-gray-700">Selected Type Details:</h3>
            <div className="space-y-2 text-sm">
              <p><strong>Label:</strong> {selectedType.label}</p>
              <p><strong>Value:</strong> {selectedType.value}</p>
              <p><strong>Final Value:</strong> {selectedType.finalValue}</p>
              <p><strong>Final Label:</strong> {selectedType.finalLabel}</p>
              {selectedType.selectedSubType && (
                <div className="mt-2 p-2 bg-blue-50 rounded">
                  <p><strong>Selected Subtype:</strong></p>
                  <p>Label: {selectedType.selectedSubType.label}</p>
                  <p>Value: {selectedType.selectedSubType.value}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 text-yellow-800">Instructions:</h3>
          <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
            <li>Select "Men Clothing" category</li>
            <li>Click on "Kurta" to see different kurta styles</li>
            <li>Click on "Suit" to see different suit styles</li>
            <li>Click on "Shirt" to see collar and cuff type options</li>
            <li>Click on "Trouser" to see different trouser styles</li>
            <li>Click on "Tuxedo" to see different tuxedo styles</li>
            <li>Click on "Sherwani" to see different sherwani styles</li>
            <li>Select different subtypes and combinations</li>
            <li>Check the selected type details below</li>
          </ul>
        </div>

        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-2 text-green-800">Available Subtypes:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-green-700 mb-2">Kurta Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-600">
                <li>Traditional Kurta</li>
                <li>Pathani Kurta</li>
                <li>Indo-Western Kurta</li>
                <li>Embroidered Kurta</li>
                <li>Plain Kurta</li>
                <li>Silk Kurta</li>
                <li>Cotton Kurta</li>
                <li>Linen Kurta</li>
                <li>Wedding Kurta</li>
                <li>Festival Kurta</li>
                <li>Modern Kurta</li>
                <li>Designer Kurta</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-green-700 mb-2">Suit Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-600">
                <li>Business Suit</li>
                <li>Formal Suit</li>
                <li>Wedding Suit</li>
                <li>Single Breasted Suit</li>
                <li>Double Breasted Suit</li>
                <li>Three-Piece Suit</li>
                <li>Two-Piece Suit</li>
                <li>Slim Fit Suit</li>
                <li>Regular Fit Suit</li>
                <li>Wool Suit</li>
                <li>Linen Suit</li>
                <li>Designer Suit</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-green-700 mb-2">Shirt Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-600">
                <li>Regular Collar - Regular Cuff</li>
                <li>Regular Collar - French Cuff</li>
                <li>Regular Collar - Barrel Cuff</li>
                <li>Spread Collar - Regular Cuff</li>
                <li>Spread Collar - French Cuff</li>
                <li>Spread Collar - Barrel Cuff</li>
                <li>Point Collar - Regular Cuff</li>
                <li>Point Collar - French Cuff</li>
                <li>Point Collar - Barrel Cuff</li>
                <li>Button-Down Collar - Regular Cuff</li>
                <li>Button-Down Collar - French Cuff</li>
                <li>Button-Down Collar - Barrel Cuff</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-green-700 mb-2">Trouser Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-600">
                <li>Formal Trousers</li>
                <li>Chino Trousers</li>
                <li>Cargo Trousers</li>
                <li>Pleated Trousers</li>
                <li>Flat Front Trousers</li>
                <li>Wide Leg Trousers</li>
                <li>Slim Fit Trousers</li>
                <li>Straight Leg Trousers</li>
                <li>Tapered Trousers</li>
                <li>Linen Trousers</li>
                <li>Wool Trousers</li>
                <li>Cotton Trousers</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-green-700 mb-2">Tuxedo Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-600">
                <li>Classic Black Tuxedo</li>
                <li>White Dinner Jacket Tuxedo</li>
                <li>Shawl Lapel Tuxedo</li>
                <li>Peak Lapel Tuxedo</li>
                <li>Notch Lapel Tuxedo</li>
                <li>Single Button Tuxedo</li>
                <li>Double Button Tuxedo</li>
                <li>Velvet Tuxedo</li>
                <li>Satin Lapel Tuxedo</li>
                <li>Modern Slim Fit Tuxedo</li>
                <li>Traditional Fit Tuxedo</li>
                <li>Colored Tuxedo</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-green-700 mb-2">Sherwani Types:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-600">
                <li>Traditional Sherwani</li>
                <li>Jodhpuri Sherwani (Bandhgala)</li>
                <li>Indo-Western Sherwani</li>
                <li>Embroidered Sherwani</li>
                <li>Plain Sherwani</li>
                <li>Silk Sherwani</li>
                <li>Velvet Sherwani</li>
                <li>Brocade Sherwani</li>
                <li>Wedding Sherwani</li>
                <li>Festival Sherwani</li>
                <li>Modern Sherwani</li>
                <li>Designer Sherwani</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestProductDropdowns;
